import { Injectable } from '@angular/core'
import { Observable, Subject, Subscription } from 'rxjs'
import {
  WebSocketService,
  AIResponse,
  InterimTranscriptEvent,
  BridgeAudioEvent,
} from '../websocket.service'
import { PersonaData } from '../persona/persona.mapping'
import { AudioQueueService } from './audio-queue.service'
import { RoleplayAssessmentParserService } from './roleplay-assessment-parser.service'
import { RoleplayAssessmentData } from './roleplay-assessment.types'

/**
 * ロールプレイWebSocketファサードサービス
 *
 * 責務:
 * - WebSocketServiceの主要イベントをドメインAPIに集約
 * - 評価データのパース・配信
 * - AIレスポンスの音声エンキューとブロードキャスト
 * - セッション開始時/終了時のサブスクリプション管理
 *
 * 依存関係:
 * - Inbound: RoleplaySessionPageComponent, CloudSttTurnCoordinatorService
 * - Outbound: WebSocketService (P0), AudioQueueService (P0)
 */
interface SessionResumeState {
  sessionId: string
  personaData: PersonaData
  dialogState: string | null
}

@Injectable({
  providedIn: 'root',
})
export class RoleplayWebSocketFacadeService {
  private resumeState: SessionResumeState | null = null

  private subscriptions = new Subscription()

  private readonly aiResponseSubject = new Subject<AIResponse>()
  private readonly bridgeAudioSubject = new Subject<BridgeAudioEvent>()
  private readonly interimTranscriptSubject =
    new Subject<InterimTranscriptEvent>()
  private readonly assessmentDataSubject = new Subject<RoleplayAssessmentData>()

  constructor(
    private webSocketService: WebSocketService,
    private audioQueue: AudioQueueService,
    private assessmentParser: RoleplayAssessmentParserService,
  ) {
    // Subscriptions will be set up in startSession()
  }

  private setupSubscriptions(): void {
    this.subscriptions.add(
      this.webSocketService.aiResponse$.subscribe((response) => {
        this.processAiResponse(response, false)
      }),
    )

    this.subscriptions.add(
      this.webSocketService.bridgeAudio$.subscribe((event) => {
        this.audioQueue.enqueue({
          type: 'filler',
          audioData: event.audioData,
          interruptible: true,
          source: 'bridge-audio',
          metadata: {
            fillerId: event.fillerId,
            duration: event.duration,
            timestamp: event.timestamp,
          },
        })
        this.bridgeAudioSubject.next(event)
      }),
    )

    this.subscriptions.add(
      this.webSocketService.interimTranscript$.subscribe((event) => {
        this.interimTranscriptSubject.next(event)
      }),
    )
  }

  /**
   * AIレスポンスの共通処理パイプライン
   *
   * 評価データパース → 音声エンキュー → AIレスポンスブロードキャストの順序で処理を実行します。
   * RxJS Subjectの同期emit特性により、コンポーネント側でSE再生トリガーと
   * conversations評価エントリ挿入がAI音声再生開始・AI会話ログ追加より先に行われます。
   */
  private processAiResponse(
    response: AIResponse,
    skipEmptyAudio: boolean,
  ): void {
    const assessmentData = this.assessmentParser.parse(response)
    if (assessmentData != null) {
      this.assessmentDataSubject.next(assessmentData)
    }

    const shouldEnqueue = !skipEmptyAudio || !!response.audioData
    if (shouldEnqueue) {
      this.audioQueue.enqueue({
        type: 'response',
        audioData: response.audioData,
        interruptible: false,
        source: 'ai-response',
        metadata: {
          text: response.response,
          timestamp: response.timestamp,
        },
      })
    }
    this.aiResponseSubject.next(response)
  }

  /**
   * デバッグ用: AIレスポンスJSONをパイプラインに注入する
   *
   * metadata.source: 'debug-injection' を自動付与し、既存のAIレスポンス処理パイプライン
   * （評価データパース → 音声エンキュー → ブロードキャスト）を実行します。
   * audioDataが空の場合はAudioQueueへのenqueueをスキップします。
   */
  injectAiResponse(response: AIResponse): void {
    if (this.resumeState == null) {
      console.warn(
        'injectAiResponse: セッションが開始されていません。startSession()を先に呼び出してください。',
      )
      return
    }

    const enrichedResponse: AIResponse = {
      ...response,
      metadata: {
        ...response.metadata,
        source: 'debug-injection',
      },
    }

    this.processAiResponse(enrichedResponse, true)
  }

  async connect(): Promise<void> {
    await this.webSocketService.connect()
  }

  disconnect(): void {
    this.webSocketService.disconnect()
  }

  async startSession(sessionId: string, persona: PersonaData): Promise<void> {
    this.subscriptions.unsubscribe()
    this.subscriptions = new Subscription()

    this.resumeState = {
      sessionId,
      personaData: persona,
      dialogState: null,
    }

    this.setupSubscriptions()
  }

  async endSession(): Promise<void> {
    this.subscriptions.unsubscribe()
    this.subscriptions = new Subscription()

    this.resumeState = null
  }

  getAiResponse$(): Observable<AIResponse> {
    return this.aiResponseSubject.asObservable()
  }

  getAssessmentData$(): Observable<RoleplayAssessmentData> {
    return this.assessmentDataSubject.asObservable()
  }

  getBridgeAudio$(): Observable<BridgeAudioEvent> {
    return this.bridgeAudioSubject.asObservable()
  }

  getInterimTranscript$(): Observable<InterimTranscriptEvent> {
    return this.interimTranscriptSubject.asObservable()
  }
}
